import {
  createFileRoute,
  Outlet,
  Link,
  useParams,
  useLocation,
  redirect,
  useNavigate,
} from '@tanstack/react-router';
import { useEffect, useRef, useState, useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './smartbanking.module.scss';
import { toggleTheme } from '../../utils/toggleTheme';
import {
  Icon,
  SidebarMenu,
  Dropdown,
  Header,
  Modal,
  Spinner,
  type SidebarMenuItem,
  type HeaderNavigationItem,
} from '@penta/penta-shared';
import { usePentaRq, useLegacyAuthGatewayFetch } from '@penta/penta-api';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { ClientContextProvider, useClient } from '../../contexts/ClientContext';
import { useRedirectToOldIb } from '../../hooks/useRedirectToOldIb';
import { ClientSelector } from '../../components/ClientSelector';
import { LanguageSwitcher } from '../../components/LanguageSwitcher';

export const Route = createFileRoute('/$locale/smartbanking')({
  beforeLoad: ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({
        to: '/login',
      });
    }
  },
  component: DashboardLayout,
});

function DashboardLayout() {
  return (
    <ClientContextProvider>
      <DashboardLayoutContent />
    </ClientContextProvider>
  );
}

function DashboardLayoutContent() {
  const { locale } = useParams({ from: Route.id });
  const { t } = useTranslation();
  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  const location = useLocation();
  const api = usePentaRq();
  const legacyApi = useLegacyAuthGatewayFetch();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { redirect: redirectToOldIb, isRedirecting } = useRedirectToOldIb();
  const { selectedClientId } = useClient();

  const { data: messagesCountData } = api.useQuery(
    'get',
    '/message/count',
    {},
    { enabled: typeof selectedClientId === 'number' },
  );

  const prevClientIdRef = useRef<number | null>(null);
  useEffect(() => {
    if (selectedClientId !== null && prevClientIdRef.current !== null) {
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey.includes('/message/count'),
      });
    }
    prevClientIdRef.current = selectedClientId;
  }, [selectedClientId, queryClient]);

  const themeItems = useMemo(
    () => [
      { id: 'light', label: <Icon name="sun" size={20} /> },
      { id: 'dark', label: <Icon name="moon" size={20} /> },
    ],
    [],
  );

  useEffect(() => {
    const stored = localStorage.getItem('theme');
    if (stored === 'light' || stored === 'dark') {
      setTheme(stored);
    }
  }, []);

  const handleChangeTheme = (newTheme: 'light' | 'dark') => {
    toggleTheme(newTheme);
    setTheme(newTheme);
  };

  const handleLogout = async () => {
    try {
      await legacyApi.DELETE('/v1/token');
    } catch (error) {
      console.error('Logout failed', error);
    } finally {
      logout();
      // Clear all queries to remove cached data
      queryClient.clear();
      // Redirect to logout page
      navigate({ to: '/logout' });
    }
  };

  const linkParams = useMemo(() => ({ locale }), [locale]);
  const linkStyle = useMemo(
    () => ({ textDecoration: 'none', color: 'inherit' }),
    [],
  );

  const sidebarItems = useMemo(
    () => [
      {
        id: 'dashboard',
        icon: <Icon name="squares2x2" size={28} />,
        label: t('sidebar.overview'),
        title: t('sidebar.overview'),
        href: `/${locale}/smartbanking/dashboard`,
        isActive:
          location.pathname === `/${locale}/smartbanking/dashboard` ||
          location.pathname.startsWith(`/${locale}/smartbanking/dashboard/`),
        testId: 'sidebar-dashboard',
      },
      {
        id: 'turnovers',
        icon: <Icon name="arrows-refresh" size={28} />,
        label: t('sidebar.turnovers'),
        title: t('sidebar.turnovers'),
        isActive: false,
        onClick: () => redirectToOldIb('TurnoverList'),
        testId: 'sidebar-turnovers',
      },
      {
        id: 'payments',
        icon: <Icon name="banknotes" size={28} />,
        label: t('sidebar.payments'),
        title: t('sidebar.payments'),
        href: `/${locale}/smartbanking/payments`,
        isActive:
          location.pathname === `/${locale}/smartbanking/payments` ||
          location.pathname.startsWith(`/${locale}/smartbanking/payments/`),
        testId: 'sidebar-payments',
      },
      {
        id: 'cards',
        icon: <Icon name="credit-card" size={28} />,
        label: t('sidebar.cards'),
        title: t('sidebar.cards'),
        isActive: false,
        onClick: () => redirectToOldIb('PaymentCardList'),
        testId: 'sidebar-cards',
      },
      {
        id: 'savings',
        icon: <Icon name="database" size={28} />,
        label: t('sidebar.savings'),
        title: t('sidebar.savings'),
        isActive: false,
        onClick: () => redirectToOldIb('TimeDepositTurnList'),
        testId: 'sidebar-savings',
      },
      {
        id: 'investments',
        icon: <Icon name="chart-bar-vertical" size={28} />,
        label: t('sidebar.investments'),
        title: t('sidebar.investments'),
        isActive: false,
        onClick: () => redirectToOldIb('PortfolioOverview'),
        testId: 'sidebar-investments',
      },
      {
        id: 'templates',
        icon: <Icon name="document" size={28} />,
        label: t('sidebar.templates'),
        title: t('sidebar.templates'),
        isActive: false,
        onClick: () => redirectToOldIb('TemplatePaymentList'),
        testId: 'sidebar-templates',
      },
      {
        id: 'settings',
        icon: <Icon name="cog-8-tooth" size={28} />,
        label: t('sidebar.settings'),
        title: t('sidebar.settings'),
        onClick: () => redirectToOldIb('DefaultSetting'),
        isActive: false,
        testId: 'sidebar-settings',
      },
      {
        id: 'info',
        icon: <Icon name="information-circle" size={28} />,
        label: t('sidebar.info'),
        title: t('sidebar.info'),
        onClick: () => redirectToOldIb('Information'),
        isActive: false,
        testId: 'sidebar-info',
      },
    ],
    [t, locale, redirectToOldIb, location.pathname],
  );

  const headerNavItems: HeaderNavigationItem[] = useMemo(
    () => [
      {
        iconName: 'phone',
        label: t('topMenu.contacts'),
        onClick: () =>
          window.open(
            'https://www.privatbanka.cz/privatni-bankovnictvi-bankeri',
            '_blank',
            'noopener,noreferrer',
          ),
        'data-testid': 'nav-contacts',
      },
      {
        iconName: 'chat-bubble-left-ellipsis',
        label: t('topMenu.messages'),
        badge: messagesCountData?.unreadCount ?? 0,
        onClick: () => redirectToOldIb('MessagesFromBank'),
        'data-testid': 'nav-messages',
      },
      {
        iconName: 'document',
        label: t('topMenu.documents'),
        onClick: () => redirectToOldIb('DocumentToSignList'),
        'data-testid': 'nav-documents',
      },
      {
        iconName: 'cog-8-tooth',
        label: t('sidebar.settings'),
        onClick: () => redirectToOldIb('DefaultSetting'),
        'data-testid': 'nav-settings',
      },
    ],
    [t, redirectToOldIb, messagesCountData],
  );

  const renderSidebarLink = useMemo(
    () =>
      (
        item: SidebarMenuItem,
        children: ReactNode,
        className: string,
        ariaCurrent?: 'page',
      ): ReactNode => {
        if (item.onClick) {
          return (
            <button
              type="button"
              onClick={item.onClick}
              className={className}
              aria-current={ariaCurrent}
              aria-label={item.label}
              data-testid={item.testId}
            >
              {children}
            </button>
          );
        }
        return (
          <Link
            params={linkParams}
            to={item.href as string}
            className={className}
            aria-current={ariaCurrent}
            data-testid={item.testId}
          >
            {children}
          </Link>
        );
      },
    [linkParams],
  );

  return (
    <div className={styles.appContainer}>
      <Header
        logo={
          <Link
            to="/$locale/smartbanking/dashboard"
            params={linkParams}
            style={linkStyle}
            aria-label={t('sidebar.logoAriaLabel')}
          >
            <Icon name="pb-logo" size={65} />
          </Link>
        }
        navigationItems={headerNavItems}
        navAriaLabel={t('topMenu.topNavigation')}
        themeControl={
          <Dropdown
            trigger={
              <button
                type="button"
                className={styles.controlTrigger}
                aria-label={t('topMenu.switchTheme')}
                data-testid="theme-toggle"
              >
                <Icon
                  name={theme === 'dark' ? 'moon' : 'sun'}
                  size={20}
                  aria-hidden="true"
                />
              </button>
            }
            items={themeItems}
            value={theme}
            onValueChange={(value) =>
              handleChangeTheme(value as 'light' | 'dark')
            }
            ariaLabel={t('topMenu.switchTheme')}
            data-testid="theme-dropdown"
          />
        }
        languageControl={<LanguageSwitcher className={styles.controlTrigger} />}
        onLogout={handleLogout}
        logoutLabel={t('topMenu.logout')}
        userSection={<ClientSelector />}
      />
      <div className={styles.smartbankingLayout}>
        {/* Sidebar */}
        <div data-theme="dark" className={styles.sidebarColumn}>
          <SidebarMenu
            items={sidebarItems}
            renderLink={renderSidebarLink}
            ariaLabel={t('sidebar.mainNavigation', 'Hlavní navigace')}
          />
        </div>

        {/* Content Outlet - Renders the child route (e.g. Overview) */}
        <main className={styles.mainContent}>
          <Outlet />
        </main>
      </div>
      <Modal
        open={isRedirecting}
        closeOnInteractOutside={false}
        closeOnEscape={false}
        aria-label={t('redirect.ariaLabel', 'Přesměrování')}
        description={t(
          'redirect.description',
          'Probíhá přesměrování do internetového bankovnictví.',
        )}
        data-testid="redirect-modal"
      >
        <Spinner size="lg" />
      </Modal>
    </div>
  );
}
